import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Search, Crown, Users as UsersIcon, Globe2, Mail, RefreshCw, Gift, X, Check, Store, Sparkles, Eye, AtSign, ShieldAlert, AlertTriangle } from "lucide-react";

const PLAN_BADGE = {
  starter: "bg-slate-100 text-slate-700",
  growth: "bg-[#FEE7E1] text-[#F26953]",
  pro: "bg-[#0F172A] text-white",
};

const TIER_COPY = {
  starter: { name: "Starter", price: 3, blurb: "1 alias · 1 recipient" },
  growth:  { name: "Growth",  price: 6, blurb: "5 aliases · 3 recipients" },
  pro:     { name: "Pro",     price: 9, blurb: "Unlimited aliases · unlimited recipients" },
};

function GrantSlotModal({ open, onClose, user, onGranted }) {
  const [tier, setTier] = useState("growth");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setTier("growth"); setQty(1); setNote(""); }
  }, [open]);

  if (!open || !user) return null;
  const isFreeUser = (user.plan || "free") === "free" && user.role !== "admin";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/grant-slot`, {
        plan_tier: tier,
        quantity: Number(qty),
        note: note.trim() || undefined,
      });
      const upgraded = isFreeUser && data.plan === "starter";
      toast.success(
        upgraded
          ? `Granted ${qty} ${TIER_COPY[tier].name} slot${qty > 1 ? "s" : ""} to ${user.email} — auto-upgraded to Starter so they can use it.`
          : `Granted ${qty} ${TIER_COPY[tier].name} slot${qty > 1 ? "s" : ""} to ${user.email}`,
      );
      onGranted(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="grant-slot-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white border border-[#E2E8F0] shadow-xl overflow-hidden">
        <div className="flex items-start justify-between p-6 border-b border-[#E2E8F0]">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#FEE7E1] grid place-items-center flex-shrink-0">
              <Gift className="h-5 w-5 text-[#F26953]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#0F172A]">Grant domain credit</h3>
              <p className="text-xs text-[#475569] mt-0.5 truncate max-w-[300px]">to <span className="font-semibold">{user.email}</span></p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="grant-slot-close"><X className="h-4 w-4" /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="text-sm font-medium text-[#0F172A] mb-2 block">Tier</label>
            <div className="space-y-2">
              {Object.entries(TIER_COPY).map(([id, t]) => (
                <button
                  type="button"
                  key={id}
                  onClick={() => setTier(id)}
                  data-testid={`grant-slot-tier-${id}`}
                  className={`w-full text-left rounded-xl border p-3.5 transition-all ${
                    tier === id ? "border-[#F26953] bg-[#FFF8F6]" : "border-[#E2E8F0] hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-bold text-[#0F172A]">{t.name}</div>
                      <div className="text-xs text-[#475569]">{t.blurb} · normally ${t.price}/mo</div>
                    </div>
                    <div className={`h-5 w-5 rounded-full border-2 grid place-items-center flex-shrink-0 ${
                      tier === id ? "border-[#F26953] bg-[#F26953]" : "border-slate-300 bg-white"
                    }`}>
                      {tier === id && <Check className="h-3 w-3 text-white" />}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-[#0F172A]">Quantity</span>
            <input
              type="number"
              min="1"
              max="100"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              data-testid="grant-slot-qty-input"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-[#E2E8F0] outline-none focus:border-[#F26953] text-sm"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-[#0F172A]">Note <span className="text-slate-400 font-normal">(optional, audit log)</span></span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. compensation for downtime"
              maxLength={200}
              data-testid="grant-slot-note-input"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-[#E2E8F0] outline-none focus:border-[#F26953] text-sm"
            />
          </label>

          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
            This is a free credit — it does NOT charge the user. Use for refunds, compensation, or comp accounts.
          </div>

          {isFreeUser && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800" data-testid="grant-slot-auto-upgrade-notice">
              <strong>Heads up:</strong> {user.email} is on the Free plan and can't add domains today. We'll auto-upgrade them to Starter (no charge) so they can actually use this credit.
            </div>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={onClose} data-testid="grant-slot-cancel"
              className="flex-1 bg-white border border-[#E2E8F0] text-[#0F172A] font-semibold py-2.5 rounded-lg hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={submitting} data-testid="grant-slot-confirm"
              className="flex-1 bg-[#F26953] text-white font-semibold py-2.5 rounded-lg hover:bg-[#D85340] disabled:opacity-60">
              {submitting ? "Granting…" : `Grant ${qty} ${TIER_COPY[tier].name} slot${qty > 1 ? "s" : ""}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function InventoryModal({ open, onClose, user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !user) { setData(null); setErr(null); return; }
    setLoading(true);
    api.get(`/admin/users/${user.id}/inventory`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, user]);

  if (!open || !user) return null;

  return (
    <div
      data-testid="admin-inventory-modal"
      className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 bg-black/40 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-2xl shadow-xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0] sticky top-0 bg-white rounded-t-2xl">
          <div className="min-w-0 pr-3">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Customer inventory</div>
            <div className="text-lg font-bold text-[#0F172A] truncate">{user.email}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="inventory-close-btn"
            className="text-slate-400 hover:text-[#0F172A] p-1 -mr-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5">
          {loading && (
            <div className="py-12 text-center text-sm text-slate-400">Loading…</div>
          )}
          {err && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{err}</div>
          )}

          {data && (
            <>
              <div className="flex gap-3 mb-5">
                <div className="flex-1 bg-slate-50 border border-[#E2E8F0] rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Domains</div>
                  <div className="text-2xl font-bold text-[#0F172A] mt-1">{data.domain_count}</div>
                </div>
                <div className="flex-1 bg-slate-50 border border-[#E2E8F0] rounded-lg p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Aliases</div>
                  <div className="text-2xl font-bold text-[#0F172A] mt-1">{data.alias_count}</div>
                </div>
              </div>

              {data.domains.length === 0 && (
                <div className="py-10 text-center text-sm text-slate-400" data-testid="inventory-empty">
                  No domains yet.
                </div>
              )}

              <div className="space-y-3" data-testid="inventory-domain-list">
                {data.domains.map((d) => (
                  <div key={d.id} className="rounded-xl border border-[#E2E8F0] overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0">
                        <Globe2 className="h-4 w-4 text-[#F26953] flex-shrink-0" />
                        <span className="font-semibold mono text-sm text-[#0F172A] truncate" data-testid={`inventory-domain-${d.domain}`}>
                          {d.domain}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${PLAN_BADGE[d.plan_tier] || PLAN_BADGE.starter}`}>
                          {d.plan_tier || "starter"}
                        </span>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                          d.verified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {d.verified ? "Verified" : "Pending"}
                        </span>
                      </div>
                    </div>
                    {d.aliases && d.aliases.length > 0 ? (
                      <ul className="divide-y divide-[#E2E8F0]">
                        {d.aliases.map((a) => (
                          <li key={a.id} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`inventory-alias-${a.alias}@${a.domain}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <AtSign className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                              <span className="mono text-[#0F172A] truncate">{a.alias}@{a.domain}</span>
                            </div>
                            <div className="text-xs text-[#475569] truncate max-w-[260px]" title={a.forward_to}>
                              → {a.forward_to}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="px-4 py-3 text-xs text-slate-400 italic">No aliases on this domain.</div>
                    )}
                  </div>
                ))}
              </div>

              {data.orphan_aliases && data.orphan_aliases.length > 0 && (
                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/50 p-3">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-2">Orphan aliases (domain removed)</div>
                  <ul className="space-y-1.5">
                    {data.orphan_aliases.map((a) => (
                      <li key={a.id} className="text-xs mono text-[#475569]">
                        {a.alias}@{a.domain} → {a.forward_to}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [mg, setMg] = useState(null);
  const [txDomain, setTxDomain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [grantTarget, setGrantTarget] = useState(null);
  const [inventoryTarget, setInventoryTarget] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/users")])
      .then(([s, u]) => { setStats(s.data); setUsers(u.data.users); })
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    api.get("/admin/mailgun-usage").then(r => setMg(r.data)).catch(() => {});
    api.get("/admin/transactional-domain").then(r => setTxDomain(r.data)).catch(() => {});
  }, []);

  const onUserGranted = (updated) => {
    setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
  };

  const onCloverToggle = async (u) => {
    const next = !(u.clover_app_enabled ?? true);
    try {
      await api.post(`/admin/users/${u.id}/clover-enable`, { enabled: next });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, clover_app_enabled: next } : x));
      toast.success(
        next
          ? `Clover app re-enabled for ${u.email}`
          : `Clover app disabled for ${u.email} — device app stops within 5 minutes.`,
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onSuspendUser = async (u) => {
    const reason = window.prompt(
      `Suspend ${u.email}?\n\nThis will:\n- Block their login + cookie sessions\n- Mark all their aliases suspended\n- Delete every Mailgun route for their domains (forwarding stops immediately)\n- Email them + email support@olyinbox.com\n\nReason (shown in audit log):`,
      "Manual admin suspension"
    );
    if (reason === null) return; // user cancelled
    try {
      await api.post(`/admin/users/${u.id}/suspend`, { reason: reason.trim() || "Manual admin suspension" });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, status: "suspended", suspended_reason: reason.trim() || "Manual admin suspension" } : x));
      toast.success(`${u.email} suspended. Routes purged, user notified.`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onUnsuspendUser = async (u) => {
    if (!window.confirm(`Unsuspend ${u.email}? Their domains and aliases will be reactivated.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/unsuspend`);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, status: "active", suspended_reason: null } : x));
      toast.success(`${u.email} unsuspended.`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = useMemo(() => {
    return users.filter(u => {
      if (filter !== "all" && u.plan !== filter) return false;
      if (search && !u.email.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [users, filter, search]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="admin-main">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-6 w-6 text-[#F26953]"/>
          <span className="text-sm font-bold tracking-[0.2em] uppercase text-[#F26953]">Admin</span>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight">Customers</h1>
            <p className="text-sm text-[#475569] mt-2">All registered users, their plan, and usage.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/admin/marketing"
              data-testid="admin-marketing-link"
              className="inline-flex items-center gap-2 text-xs font-semibold text-white bg-[#F26953] hover:bg-[#D85340] px-4 py-2 rounded-lg shadow-sm"
            >
              <Sparkles className="h-3.5 w-3.5" /> Marketing engine
            </Link>
            <button
              onClick={async () => {
                try {
                  const { data } = await api.post("/admin/run-verifier");
                  toast.success(`Verifier swept ${data.checked} domain(s) — ${data.newly_verified} newly verified.`);
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              data-testid="admin-run-verifier"
              className="inline-flex items-center gap-2 text-xs font-semibold text-[#0F172A] bg-white border border-[#E2E8F0] px-4 py-2 rounded-lg hover:bg-slate-50"
            >
              <RefreshCw className="h-3.5 w-3.5"/> Run DNS verifier now
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
          <StatCard icon={UsersIcon} label="Total users" value={stats?.total_users ?? "—"} />
          <StatCard icon={Globe2} label="Total domains" value={stats?.total_domains ?? "—"} />
          <StatCard icon={Mail} label="Total aliases" value={stats?.total_aliases ?? "—"} />
          <StatCard icon={RefreshCw} label="DNS pending" value={stats?.unverified_mailgun ?? "—"} />
          <StatCard icon={Crown} label="Pro users" value={stats?.by_plan?.pro ?? "—"} accent />
        </div>

        {/* Mailgun sending usage vs. hourly limit */}
        <div className="mt-6 rounded-2xl bg-white border border-[#E2E8F0] p-5" data-testid="admin-mailgun-usage">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Mailgun sending usage</div>
              {mg?.ok ? (
                <div className="mt-1 text-sm text-[#0F172A]">
                  <span className="font-mono font-bold">{mg.last_hour_sent}</span>
                  <span className="text-slate-400"> / {mg.hourly_limit} msgs this hour</span>
                  <span className={`ml-3 text-xs font-semibold ${mg.utilization_pct >= 80 ? "text-amber-600" : "text-slate-400"}`}>
                    {mg.utilization_pct}% utilized
                  </span>
                </div>
              ) : (
                <div className="mt-1 text-sm text-slate-400">
                  {mg === null ? "Loading…" : (mg?.error || "Mailgun stats unavailable")}
                </div>
              )}
            </div>
            {mg?.ok && (
              <div className="flex-1 max-w-xs ml-auto">
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full ${mg.utilization_pct >= 80 ? "bg-amber-500" : mg.utilization_pct >= 50 ? "bg-[#F26953]" : "bg-emerald-500"}`}
                    style={{ width: `${Math.min(100, mg.utilization_pct)}%` }}
                  />
                </div>
                <div className="text-[10px] text-slate-400 mt-1.5">Last 24h: {mg.total_accepted} accepted · {mg.total_delivered} delivered · {mg.total_failed} failed</div>
              </div>
            )}
          </div>
          {mg?.ok && mg.utilization_pct >= 70 && (
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              Approaching Mailgun's hourly limit. Contact Mailgun support to request a raise (typical: 10,000/hour for forwarding use-cases).
            </div>
          )}
        </div>

        {/* Mailgun bounce-rate alert (5%+ triggers compliance auto-suspension) */}
        {mg?.ok && (
          <div
            className={`mt-4 rounded-2xl bg-white border p-5 ${
              mg.bounce_severity === "critical" ? "border-red-300" :
              mg.bounce_severity === "warning"  ? "border-amber-300" :
                                                   "border-[#E2E8F0]"
            }`}
            data-testid="admin-bounce-rate"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Account bounce rate (last 24h)</div>
                <div className="mt-1 flex items-baseline gap-3">
                  <span
                    className={`text-2xl font-bold mono ${
                      mg.bounce_severity === "critical" ? "text-red-600" :
                      mg.bounce_severity === "warning"  ? "text-amber-600" :
                                                          "text-emerald-600"
                    }`}
                    data-testid="admin-bounce-pct"
                  >
                    {mg.bounce_pct}%
                  </span>
                  <span className="text-xs text-slate-400">
                    {mg.total_failed} failed / {mg.total_accepted} accepted
                  </span>
                </div>
              </div>
              <div className="flex-1 max-w-xs ml-auto">
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full transition-all ${
                      mg.bounce_severity === "critical" ? "bg-red-500" :
                      mg.bounce_severity === "warning"  ? "bg-amber-500" :
                                                          "bg-emerald-500"
                    }`}
                    style={{ width: `${Math.min(100, (mg.bounce_pct / 8) * 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-slate-400 mt-1.5">
                  <span className="text-emerald-600">&lt; 3% healthy</span>
                  <span className="text-amber-600">3-5% caution</span>
                  <span className="text-red-600">5%+ risk</span>
                </div>
              </div>
            </div>
            {mg.bounce_severity === "critical" && (
              <div className="mt-3 text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="admin-bounce-critical-alert">
                <strong>Critical:</strong> Mailgun's compliance team auto-suspends accounts that sustain bounce rates above 5%. Investigate suspicious users immediately and consider adding email validation to high-risk forward targets.
              </div>
            )}
            {mg.bounce_severity === "warning" && (
              <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="admin-bounce-warning-alert">
                <strong>Heads up:</strong> Bounce rate climbing toward Mailgun's 5% compliance threshold. Review recent signups for suspicious patterns in the Suspicious Activity panel below.
              </div>
            )}
          </div>
        )}

        {/* Dedicated transactional domain (for password resets, invites, alerts) */}
        {txDomain?.configured && (
          <div className="mt-4 rounded-2xl bg-white border border-[#E2E8F0] p-5" data-testid="admin-transactional-domain">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Transactional email domain</div>
                <div className="mt-1 text-sm text-[#0F172A] mono font-semibold truncate">{txDomain.domain}</div>
              </div>
              <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
                txDomain.active ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"
              }`}>
                {txDomain.active ? "Active" : txDomain.state}
              </span>
            </div>
            {!txDomain.active && (
              <>
                <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  Add these DNS records to <span className="mono font-semibold">olyinbox.com</span> to activate un-restricted transactional email (password resets, invites, alerts). Until active, we fall back to Mailgun's sandbox which only sends to pre-authorized recipients.
                </div>
                <div className="mt-3 space-y-2">
                  {(txDomain.sending || []).filter(r => !r.valid).map((r, i) => (
                    <div key={i} className="bg-slate-50 border border-[#E2E8F0] rounded-lg p-3 text-[11px]">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-[#0F172A] uppercase">{r.type}</span>
                        <span className="text-amber-600 font-semibold">pending</span>
                      </div>
                      <div className="mono text-slate-500">Host: {r.name || "(root)"}</div>
                      <div className="mono text-slate-700 break-all mt-0.5">Value: {r.value}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by email…"
              data-testid="admin-search-input"
              className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#E2E8F0] outline-none focus:border-[#F26953] text-sm bg-white"/>
          </div>
          <div className="inline-flex bg-white rounded-lg border border-[#E2E8F0] p-1" data-testid="admin-plan-filter">
            {[["all", "All"], ["starter", "Starter"], ["growth", "Growth"], ["pro", "Pro"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)}
                data-testid={`admin-filter-${k}`}
                className={`text-xs font-semibold px-3 py-1.5 rounded ${
                  filter === k ? "bg-[#F26953] text-white" : "text-[#475569] hover:bg-slate-50"
                }`}>{l}</button>
            ))}
          </div>
        </div>

        {/* Users table */}
        <div className="mt-6 rounded-2xl bg-white border border-[#E2E8F0] overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-sm text-slate-400">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-3 font-bold">User</th>
                    <th className="text-left px-5 py-3 font-bold">Plan</th>
                    <th className="text-left px-5 py-3 font-bold">Cycle</th>
                    <th className="text-left px-5 py-3 font-bold">Domains</th>
                    <th className="text-left px-5 py-3 font-bold">Aliases</th>
                    <th className="text-left px-5 py-3 font-bold">Joined</th>
                    <th className="text-left px-5 py-3 font-bold">Clover</th>
                    <th className="text-right px-5 py-3 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {filtered.map(u => {
                    const slots = u.extra_domain_slots || { starter: 0, growth: 0, pro: 0 };
                    const slotParts = [];
                    if (slots.starter) slotParts.push(`${slots.starter}S`);
                    if (slots.growth)  slotParts.push(`${slots.growth}G`);
                    if (slots.pro)     slotParts.push(`${slots.pro}P`);
                    return (
                    <tr key={u.id} data-testid={`admin-user-row-${u.email}`} className="hover:bg-slate-50/50">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-[#0F172A]">{u.email}</div>
                          {u.role === "admin" && <span className="inline-flex items-center gap-1 text-xs bg-[#0F172A] text-white px-2 py-0.5 rounded font-semibold"><Crown className="h-3 w-3"/>Admin</span>}
                          {u.status === "suspended" && (
                            <span
                              data-testid={`admin-suspended-badge-${u.email}`}
                              title={u.suspended_reason || "Suspended"}
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 px-2 py-0.5 rounded"
                            >
                              <ShieldAlert className="h-3 w-3"/>Suspended
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[#475569] mt-0.5">{u.name}</div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded ${PLAN_BADGE[u.plan] || PLAN_BADGE.starter}`}>{u.plan}</span>
                      </td>
                      <td className="px-5 py-4 text-[#475569] capitalize">{u.billing_cycle}</td>
                      <td className="px-5 py-4 font-mono">
                        <button
                          onClick={() => setInventoryTarget(u)}
                          data-testid={`admin-view-inventory-${u.email}`}
                          className="text-[#0F172A] hover:text-[#F26953] hover:underline font-mono"
                          title="View all domains for this user"
                        >
                          {u.domain_count}
                        </button>
                        {u.role === "admin"
                          ? <span className="text-slate-400 text-xs"> /∞</span>
                          : (slotParts.length
                              ? <span className="text-[#F26953] text-xs font-semibold ml-1" title="Extra domain slots: S=Starter G=Growth P=Pro">+{slotParts.join("·")}</span>
                              : null)
                        }
                      </td>
                      <td className="px-5 py-4 font-mono">
                        <button
                          onClick={() => setInventoryTarget(u)}
                          data-testid={`admin-view-aliases-${u.email}`}
                          className="text-[#0F172A] hover:text-[#F26953] hover:underline font-mono"
                          title="View all aliases for this user"
                        >
                          {u.alias_count}
                        </button>
                        {u.role === "admin" && <span className="text-slate-400 text-xs"> /∞</span>}
                      </td>
                      <td className="px-5 py-4 text-[#475569] text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-4">
                        {u.clover_merchant_id ? (
                          <button
                            onClick={() => onCloverToggle(u)}
                            data-testid={`admin-clover-toggle-${u.email}`}
                            title={`Merchant ${u.clover_merchant_id}`}
                            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors ${
                              (u.clover_app_enabled ?? true)
                                ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                                : "bg-slate-100 text-slate-500 hover:bg-slate-200 border border-slate-200"
                            }`}
                          >
                            <Store className="h-3.5 w-3.5" />
                            {(u.clover_app_enabled ?? true) ? "Enabled" : "Disabled"}
                          </button>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={() => setInventoryTarget(u)}
                            data-testid={`admin-view-btn-${u.email}`}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#475569] hover:text-[#0F172A] hover:bg-slate-100 px-2.5 py-1.5 rounded-lg transition-colors"
                            title="View domains & aliases"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </button>
                          {u.role !== "admin" && (
                            <button
                              onClick={() => setGrantTarget(u)}
                              data-testid={`admin-grant-slot-btn-${u.email}`}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F26953] hover:bg-[#FEE7E1] px-2.5 py-1.5 rounded-lg transition-colors"
                              title="Grant a free domain slot"
                            >
                              <Gift className="h-3.5 w-3.5" />
                              Grant credit
                            </button>
                          )}
                          {u.role !== "admin" && (
                            u.status === "suspended" ? (
                              <button
                                onClick={() => onUnsuspendUser(u)}
                                data-testid={`admin-unsuspend-user-btn-${u.email}`}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2.5 py-1.5 rounded-lg transition-colors"
                                title="Reactivate this account + restore aliases"
                              >
                                <Check className="h-3.5 w-3.5" />
                                Unsuspend
                              </button>
                            ) : (
                              <button
                                onClick={() => onSuspendUser(u)}
                                data-testid={`admin-suspend-user-btn-${u.email}`}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 border border-transparent hover:border-red-200 px-2.5 py-1.5 rounded-lg transition-colors"
                                title="Suspend account — purges all Mailgun routes"
                              >
                                <ShieldAlert className="h-3.5 w-3.5" />
                                Suspend
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-12 text-sm text-slate-400">No users match those filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <PromosSection />
          <SuspiciousActivitySection />
        </div>
      </main>

      <GrantSlotModal
        open={!!grantTarget}
        user={grantTarget}
        onClose={() => setGrantTarget(null)}
        onGranted={onUserGranted}
      />
      <InventoryModal
        open={!!inventoryTarget}
        user={inventoryTarget}
        onClose={() => setInventoryTarget(null)}
      />
    </div>
  );
}

// ---------------- Promos / coupon codes ----------------
function PromosSection() {
  const [items, setItems] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    setItems(null);
    api.get(`/admin/promos${includeArchived ? "?include_archived=true" : ""}`)
      .then((r) => setItems(r.data.items || []))
      .catch((e) => { toast.error(formatApiError(e)); setItems([]); });
  };
  useEffect(refresh, [includeArchived]);

  const archive = async (promo, isArchived) => {
    if (busy) return;
    const action = isArchived ? "reactivate" : "archive";
    if (!window.confirm(`${isArchived ? "Reactivate" : "Archive"} code "${promo.code}"?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/promos/${promo.stripe_promo_id}/${action}`);
      toast.success(`${promo.code} ${isArchived ? "reactivated" : "archived"}.`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-12" data-testid="admin-promos-section">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] tracking-tight">Promo codes</h2>
          <p className="text-sm text-[#475569] mt-1">Coupon &amp; affiliate codes. Stripe-backed. Customers apply at signup or checkout.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-[#475569]">
            <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="admin-promos-include-archived" />
            Show archived
          </label>
          <button
            onClick={() => setCreating(true)}
            data-testid="admin-promos-new-btn"
            className="inline-flex items-center gap-2 text-xs font-semibold text-white bg-[#F26953] hover:bg-[#D85340] px-4 py-2 rounded-lg shadow-sm"
          >
            <Gift className="h-3.5 w-3.5" /> New code
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        {items === null ? (
          <div className="py-14 text-center text-sm text-slate-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-14 text-center" data-testid="admin-promos-empty">
            <Gift className="h-8 w-8 mx-auto text-slate-300" />
            <div className="text-sm text-[#475569] mt-3">No promo codes yet. Create your first to share with customers or affiliates.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-promos-table">
              <thead className="bg-slate-50 text-[#475569] uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Code</th>
                  <th className="text-left px-5 py-3 font-bold">Discount</th>
                  <th className="text-left px-5 py-3 font-bold">Conditions</th>
                  <th className="text-left px-5 py-3 font-bold">Used</th>
                  <th className="text-left px-5 py-3 font-bold">Status</th>
                  <th className="text-right px-5 py-3 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {items.map((p) => {
                  const isArchived = !p.active;
                  return (
                    <tr key={p.stripe_promo_id} className="hover:bg-slate-50/60">
                      <td className="px-5 py-4">
                        <div className="font-mono font-semibold text-[#0F172A]" data-testid={`promo-row-${p.code}`}>{p.code}</div>
                        {p.notes && <div className="text-xs text-[#94A3B8] mt-0.5 line-clamp-1 max-w-xs" title={p.notes}>{p.notes}</div>}
                      </td>
                      <td className="px-5 py-4 font-semibold text-[#F26953]">{p.discount_label}</td>
                      <td className="px-5 py-4 text-xs text-[#475569]">
                        <div className="space-y-0.5">
                          {p.duration_in_months ? <div>For {p.duration_in_months} mo.</div> : <div>{p.duration || "once"}</div>}
                          {p.first_time_only && <div>New customers only</div>}
                          {p.max_redemptions && <div>Max {p.max_redemptions} uses</div>}
                          {p.expires_at && <div>Until {new Date(p.expires_at).toLocaleDateString()}</div>}
                        </div>
                      </td>
                      <td className="px-5 py-4 font-mono text-[#0F172A]">
                        {p.redemption_count || 0}
                        {p.max_redemptions ? <span className="text-slate-400 text-xs"> /{p.max_redemptions}</span> : null}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                          isArchived ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"
                        }`}>{isArchived ? "Archived" : "Active"}</span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={() => { navigator.clipboard.writeText(p.code); toast.success(`Copied "${p.code}"`); }}
                            data-testid={`promo-copy-${p.code}`}
                            className="text-xs font-semibold text-[#475569] hover:bg-slate-100 px-2.5 py-1.5 rounded-lg"
                          >
                            Copy
                          </button>
                          <button
                            onClick={() => {
                              const base = window.location.origin;
                              navigator.clipboard.writeText(`${base}/signup?promo=${p.code}`);
                              toast.success(`Affiliate link copied`);
                            }}
                            data-testid={`promo-link-${p.code}`}
                            className="text-xs font-semibold text-[#475569] hover:bg-slate-100 px-2.5 py-1.5 rounded-lg"
                          >
                            Link
                          </button>
                          <button
                            onClick={() => archive(p, isArchived)}
                            data-testid={`promo-archive-${p.code}`}
                            className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg ${
                              isArchived ? "text-emerald-700 hover:bg-emerald-50" : "text-red-700 hover:bg-red-50"
                            }`}
                            disabled={busy}
                          >
                            {isArchived ? "Reactivate" : "Archive"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NewPromoModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => { setCreating(false); refresh(); }}
      />
    </section>
  );
}

// ---------------- Suspicious activity (abuse detector) ----------------
function SuspiciousActivitySection() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // user_id currently being unsuspended

  const refresh = () => {
    setData(null);
    api.get("/admin/abuse/events")
      .then((r) => setData(r.data))
      .catch((e) => { toast.error(formatApiError(e)); setData({ suspended: [], warnings: [] }); });
  };
  useEffect(refresh, []);

  const unsuspend = async (u) => {
    if (!window.confirm(`Unsuspend ${u.email}? Their domains and aliases will be reactivated.`)) return;
    setBusy(u.id);
    try {
      await api.post(`/admin/users/${u.id}/unsuspend`);
      toast.success(`${u.email} unsuspended`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const suspended = data?.suspended || [];
  const warnings = data?.warnings || [];

  return (
    <section className="mt-12" data-testid="admin-abuse-section">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-[#F26953]" /> Suspicious activity
          </h2>
          <p className="text-sm text-[#475569] mt-1">
            Accounts auto-suspended by the abuse detector + sub-threshold warning signals. Unsuspend false positives in one click.
          </p>
        </div>
        <button
          onClick={refresh}
          data-testid="admin-abuse-refresh"
          className="inline-flex items-center gap-2 text-xs font-semibold text-[#475569] hover:bg-slate-100 px-3 py-2 rounded-lg"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Suspended users */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E2E8F0] bg-slate-50 flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider font-bold text-slate-500">
            Suspended accounts {suspended.length > 0 && `(${suspended.length})`}
          </span>
        </div>
        {data === null ? (
          <div className="py-10 text-center text-sm text-slate-400">Loading…</div>
        ) : suspended.length === 0 ? (
          <div className="py-10 text-center" data-testid="admin-abuse-suspended-empty">
            <ShieldCheck className="h-8 w-8 mx-auto text-emerald-400" />
            <div className="text-sm text-[#475569] mt-3">No suspended accounts. Clean house.</div>
          </div>
        ) : (
          <div className="divide-y divide-[#E2E8F0]">
            {suspended.map((u) => (
              <div key={u.id} className="p-5 hover:bg-slate-50/50" data-testid={`admin-suspended-row-${u.email}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-[#0F172A]">{u.email}</div>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-red-100 text-red-700">Suspended</span>
                      <span className="text-xs text-slate-400">
                        {u.domain_count} domain{u.domain_count === 1 ? "" : "s"} · {u.alias_count} alias{u.alias_count === 1 ? "" : "es"}
                      </span>
                    </div>
                    <div className="text-xs text-[#475569] mt-1">
                      {u.suspended_reason || "No reason recorded"}
                    </div>
                    {u.signals && u.signals.length > 0 && (
                      <ul className="mt-2 space-y-0.5">
                        {u.signals.map((s, i) => (
                          <li key={i} className="text-[11px] text-slate-500 flex items-start gap-1.5">
                            <span className="text-[#F26953] mt-0.5">•</span>
                            <span><span className="mono">[+{s.weight}]</span> {s.label}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="text-[10px] text-slate-400 mt-2">
                      Suspended {u.suspended_at ? new Date(u.suspended_at).toLocaleString() : "—"} ·
                      joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </div>
                  </div>
                  <button
                    onClick={() => unsuspend(u)}
                    disabled={busy === u.id}
                    data-testid={`admin-unsuspend-btn-${u.email}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-1.5 rounded-lg disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" />
                    {busy === u.id ? "Unsuspending…" : "Unsuspend"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Warn-level events */}
      {data !== null && warnings.length > 0 && (
        <div className="mt-5 bg-white border border-amber-200 rounded-2xl overflow-hidden" data-testid="admin-abuse-warnings">
          <div className="px-5 py-3 border-b border-amber-200 bg-amber-50 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider font-bold text-amber-800 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              Warnings (sub-suspension threshold) — {warnings.length}
            </span>
            <span className="text-[10px] text-amber-700">Score ≥ {data?.thresholds?.warn ?? 4} but &lt; {data?.thresholds?.block ?? 7}</span>
          </div>
          <div className="divide-y divide-amber-100 max-h-96 overflow-y-auto">
            {warnings.slice(0, 50).map((w) => (
              <div key={w.id} className="p-4 text-xs" data-testid={`admin-warning-row-${w.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 mr-2">
                      {w.kind}
                    </span>
                    <span className="mono font-semibold text-[#0F172A]">{w.target}</span>
                    <span className="text-slate-400 ml-2">by {w.user_email || "unknown"}</span>
                  </div>
                  <div className="text-[10px] text-amber-700 font-semibold">
                    score {w.score} · {w.created_at ? new Date(w.created_at).toLocaleString() : "—"}
                  </div>
                </div>
                {w.signals && w.signals.length > 0 && (
                  <ul className="mt-1.5 space-y-0.5 pl-4">
                    {w.signals.map((s, i) => (
                      <li key={i} className="text-[11px] text-slate-500">
                        <span className="mono text-amber-700">[+{s.weight}]</span> {s.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function NewPromoModal({ open, onClose, onCreated }) {
  const [code, setCode] = useState("");
  const [kind, setKind] = useState("free_months");
  const [value, setValue] = useState(3);
  const [duration, setDuration] = useState("once");
  const [durationMonths, setDurationMonths] = useState(3);
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [firstTimeOnly, setFirstTimeOnly] = useState(true);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [packs, setPacks] = useState([]);
  const [packKey, setPackKey] = useState("validations_100");

  useEffect(() => {
    if (open) {
      setCode(""); setKind("free_months"); setValue(3); setDuration("once");
      setDurationMonths(3); setMaxRedemptions(""); setExpiresAt("");
      setFirstTimeOnly(true); setNotes(""); setSubmitting(false);
      setPackKey("validations_100");
      // Lazy-load validation packs the first time this modal opens.
      if (packs.length === 0) {
        api.get("/validations/packs")
          .then((r) => setPacks(r.data.packs || []))
          .catch(() => {/* non-fatal */});
      }
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        code: code.trim().toUpperCase(),
        kind,
        first_time_only: firstTimeOnly,
        notes: notes.trim() || null,
      };
      if (kind === "validations") {
        payload.pack_key = packKey;
      } else {
        payload.value = Number(value);
        if (kind !== "free_months") {
          payload.duration = duration;
          if (duration === "repeating") payload.duration_in_months = Number(durationMonths);
          if (kind === "amount") payload.currency = "usd";
        }
      }
      if (maxRedemptions) payload.max_redemptions = Number(maxRedemptions);
      if (expiresAt) payload.expires_at = new Date(expiresAt).toISOString();
      await api.post("/admin/promos", payload);
      toast.success(`Promo code "${payload.code}" created.`);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const valueLabel = kind === "free_months" ? "Free months" : kind === "percent" ? "Percent off (1-100)" : "Amount off ($)";
  const fmtPackOption = (p) =>
    `${p.credits.toLocaleString()} credits — normally $${(p.price_cents / 100).toFixed(p.price_cents % 100 ? 2 : 0)} (100% off)`;

  return (
    <div
      data-testid="admin-new-promo-modal"
      className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 bg-black/40 overflow-y-auto"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl w-full max-w-lg shadow-xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]">
          <div>
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500">New promo code</div>
            <div className="text-lg font-bold text-[#0F172A]">Create a discount</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-[#0F172A] p-1 -mr-1"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-4 text-sm">
          <div>
            <label className="block text-xs font-semibold text-[#475569] mb-1">Code (customers type this)</label>
            <input
              type="text"
              required
              minLength={3}
              maxLength={50}
              pattern="[A-Za-z0-9_\-]+"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. LAUNCH3FREE"
              data-testid="new-promo-code-input"
              className="w-full font-mono uppercase rounded-lg border border-[#E2E8F0] px-3 py-2 outline-none focus:ring-2 focus:ring-[#F26953]/40"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#475569] mb-1">Discount type</label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              data-testid="new-promo-kind"
              className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 outline-none focus:ring-2 focus:ring-[#F26953]/40 bg-white"
            >
              <option value="free_months">Free months (100% off, N months)</option>
              <option value="percent">Percent off</option>
              <option value="amount">Amount off ($)</option>
              <option value="validations">Free validation credits</option>
            </select>
          </div>

          {kind === "validations" ? (
            <div data-testid="new-promo-pack-block" className="space-y-2">
              <label className="block text-xs font-semibold text-[#475569] mb-1">Credit bundle</label>
              <select
                value={packKey}
                onChange={(e) => setPackKey(e.target.value)}
                data-testid="new-promo-pack-key"
                className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 outline-none focus:ring-2 focus:ring-[#F26953]/40 bg-white"
              >
                {(packs.length ? packs : [
                  { key: "validations_100", credits: 100, price_cents: 500 },
                  { key: "validations_500", credits: 500, price_cents: 2000 },
                  { key: "validations_2500", credits: 2500, price_cents: 7500 },
                  { key: "validations_10000", credits: 10000, price_cents: 20000 },
                ]).map((p) => (
                  <option key={p.key} value={p.key}>{fmtPackOption(p)}</option>
                ))}
              </select>
              <div className="text-[11px] text-[#94A3B8] bg-amber-50 border border-amber-100 rounded p-2">
                Customer types this code at the validation-pack checkout and receives the selected bundle free.
                The coupon is locked to this single pack, so it can&apos;t be used on subscriptions.
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">{valueLabel}</label>
                  <input
                    type="number"
                    min={1}
                    max={kind === "percent" ? 100 : kind === "free_months" ? 60 : 9999}
                    required
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    data-testid="new-promo-value"
                    className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 outline-none focus:ring-2 focus:ring-[#F26953]/40"
                  />
                </div>
                {kind !== "free_months" && (
                  <div>
                    <label className="block text-xs font-semibold text-[#475569] mb-1">Applies</label>
                    <select
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 outline-none focus:ring-2 focus:ring-[#F26953]/40 bg-white"
                    >
                      <option value="once">Once (first payment)</option>
                      <option value="repeating">Repeating (N months)</option>
                      <option value="forever">Forever</option>
                    </select>
                  </div>
                )}
              </div>

              {kind !== "free_months" && duration === "repeating" && (
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Duration (months)</label>
                  <input
                    type="number"
                    min={1}
                    max={60}
                    value={durationMonths}
                    onChange={(e) => setDurationMonths(e.target.value)}
                    className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 outline-none focus:ring-2 focus:ring-[#F26953]/40"
                  />
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#475569] mb-1">Max uses total (optional)</label>
              <input
                type="number"
                min={1}
                value={maxRedemptions}
                onChange={(e) => setMaxRedemptions(e.target.value)}
                placeholder="Unlimited"
                className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 outline-none focus:ring-2 focus:ring-[#F26953]/40"
              />
              <div className="text-[10px] text-[#94A3B8] mt-1">Total across all customers, not per user.</div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#475569] mb-1">Expires (optional)</label>
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 outline-none focus:ring-2 focus:ring-[#F26953]/40"
              />
            </div>
          </div>

          <label className="flex items-start gap-2 text-xs text-[#475569]">
            <input
              type="checkbox"
              checked={firstTimeOnly}
              onChange={(e) => setFirstTimeOnly(e.target.checked)}
              className="mt-0.5"
              data-testid="new-promo-first-time"
            />
            <span>
              <span className="font-semibold text-[#0F172A]">First-time customers only</span> —
              blocks anyone who's already had a paid subscription. Recommended for "one per account" promos.
            </span>
          </label>

          <div>
            <label className="block text-xs font-semibold text-[#475569] mb-1">Internal notes (optional)</label>
            <input
              type="text"
              maxLength={200}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. ProductHunt launch, Matt's podcast affiliate"
              className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 outline-none focus:ring-2 focus:ring-[#F26953]/40"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#E2E8F0]">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-[#475569] hover:bg-slate-100 px-4 py-2 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="new-promo-submit"
            className="inline-flex items-center gap-2 bg-[#F26953] hover:bg-[#D85340] text-white text-sm font-semibold px-5 py-2 rounded-lg disabled:opacity-60"
          >
            {submitting ? "Creating…" : <>Create code <Check className="h-4 w-4" /></>}
          </button>
        </div>
      </form>
    </div>
  );
}


function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-xl border p-5 ${accent ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-[#E2E8F0]"}`}>
      <div className={`text-xs uppercase tracking-wider font-bold ${accent ? "text-slate-400" : "text-slate-400"} flex items-center gap-1.5`}>
        {Icon && <Icon className="h-3.5 w-3.5"/>}
        {label}
      </div>
      <div className={`mt-2 text-3xl font-bold ${accent ? "text-white" : "text-[#0F172A]"}`}>{value}</div>
    </div>
  );
}
